import React from "react";
import { Helmet } from "react-helmet-async";

interface TechnicalSEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  structuredData?: Record<string, any>;
  keywords?: string;
  pageType?: 'homepage' | 'blog' | 'service' | 'location' | 'product' | 'contact' | 'about' | 'default';
  faqs?: Array<{ question: string; answer: string }>;
  lastModified?: string;
  priority?: number;
  alternates?: Array<{ hreflang: string; href: string }>;
  breadcrumbs?: Array<{ name: string; url: string }>;
}

export function TechnicalSEO({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = "/og-image.png",
  structuredData,
  keywords = "secondary glazing london, acoustic glass soundproofing, heritage window insulation, listed building secondary glazing, 10.8mm acoustic laminate glass",
  pageType = 'default',
  faqs,
  lastModified,
  priority = 0.8,
  alternates,
  breadcrumbs,
}: TechnicalSEOProps) {
  const siteTitle = "London Secondary Glazing";
  const fullTitle = title.toLowerCase().includes("london") ? title : `${title} | ${siteTitle}`;
  const siteUrl = "https://www.londonsecondaryglazing.co.uk";
  // Normalise to the canonical origin with no trailing slash (matches the 301 policy).
  const normalizedCanonical = (() => {
    if (!canonical) return undefined;
    let path: string;
    try {
      const u = new URL(canonical, siteUrl);
      path = u.pathname + u.search;
    } catch {
      path = canonical.startsWith('/') ? canonical : `/${canonical}`;
    }
    if (path.length > 1 && path.endsWith('/')) path = path.replace(/\/+$/, '');
    return `${siteUrl}${path}`;
  })();
  const fullCanonical = normalizedCanonical || siteUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  
  // Enhanced structured data with multiple schemas
  const getEnhancedStructuredData = () => {
    const baseOrganization = {
      "@context": "https://schema.org",
      "@type": "HomeAndConstructionBusiness",
      "@id": `${siteUrl}/#business`,
      "parentOrganization": { "@id": `${siteUrl}/#organization` },
      "name": "London Secondary Glazing",
      "description": "Professional secondary glazing solutions in London for noise reduction, thermal insulation, and heritage preservation.",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/og-image.png`,
        "width": 1200,
        "height": 630
      },
      "telephone": "+442070601572",
      "email": "info@londonsecondaryglazing.co.uk",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Arch 141, MacFarlane Road",
        "addressLocality": "London",
        "postalCode": "W12 7LA",
        "addressRegion": "Greater London",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "51.5074",
        "longitude": "-0.1278"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      "areaServed": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "51.5074",
          "longitude": "-0.1278"
        },
        "geoRadius": "50000"
      },
      "priceRange": "£££",
      "sameAs": [
        "https://www.facebook.com/londonsecondaryglazing",
        "https://www.twitter.com/ldnsecondaryglazing",
        "https://www.instagram.com/londonsecondaryglazing",
        "https://www.linkedin.com/company/london-secondary-glazing"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Secondary Glazing Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Noise Reduction Secondary Glazing",
              "description": "Professional secondary glazing for noise reduction up to 80%"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Thermal Insulation Secondary Glazing",
              "description": "Energy efficient secondary glazing for thermal insulation"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Heritage Window Preservation",
              "description": "Specialized secondary glazing for listed buildings and heritage properties"
            }
          }
        ]
      }
    };

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      "name": "London Secondary Glazing",
      "description": "Professional secondary glazing solutions in London",
      "url": siteUrl,
      "inLanguage": "en-GB",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${siteUrl}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${fullCanonical}#webpage`,
      "name": fullTitle,
      "description": description,
      "url": fullCanonical,
      "inLanguage": "en-GB",
      "isPartOf": {
        "@id": `${siteUrl}/#website`
      },
      "about": {
        "@id": `${siteUrl}/#organization`
      },
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": fullOgImage,
        "width": 1200,
        "height": 630
      },
      "datePublished": lastModified || new Date().toISOString(),
      "dateModified": lastModified || new Date().toISOString(),
      "author": {
        "@id": `${siteUrl}/#organization`
      },
      "publisher": {
        "@id": `${siteUrl}/#organization`
      }
    };

    // Add breadcrumb schema if breadcrumbs are provided
    const breadcrumbSchema = breadcrumbs ? {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`
      }))
    } : null;

    // Add FAQ schema if FAQs are provided
    const faqSchema = faqs && faqs.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    } : null;

    // Page-specific schema based on pageType
    let pageSpecificSchema = null;
    
    switch (pageType) {
      case 'service':
        pageSpecificSchema = {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": title,
          "description": description,
          "provider": {
            "@id": `${siteUrl}/#organization`
          },
          "areaServed": {
            "@type": "GeoCircle",
            "geoMidpoint": {
              "@type": "GeoCoordinates",
              "latitude": "51.5074",
              "longitude": "-0.1278"
            },
            "geoRadius": "50000"
          },
          "url": fullCanonical,
          "serviceType": "Secondary Glazing Installation",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "GBP",
            "priceRange": "£££",
            "availability": "https://schema.org/InStock"
          }
        };
        break;
      
      case 'location':
        const locationName = title.includes("Secondary Glazing") 
          ? title.split("Secondary Glazing")[0].trim() 
          : "London";
        
        pageSpecificSchema = {
          "@context": "https://schema.org",
          "@type": "Place",
          "name": locationName,
          "description": description,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": locationName,
            "addressRegion": "London",
            "addressCountry": "GB"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "51.5074",
            "longitude": "-0.1278"
          }
        };
        break;
      
      case 'product':
        pageSpecificSchema = {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": title,
          "description": description,
          "image": fullOgImage,
          "brand": {
            "@type": "Brand",
            "name": "London Secondary Glazing"
          },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "GBP",
            "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
            "availability": "https://schema.org/InStock",
            "url": fullCanonical
          }
        };
        break;
    }

    // Combine all schemas
    const schemas: any[] = [baseOrganization, websiteSchema, webPageSchema];
    
    if (breadcrumbSchema) schemas.push(breadcrumbSchema);
    if (faqSchema) schemas.push(faqSchema);
    if (pageSpecificSchema) schemas.push(pageSpecificSchema);
    if (structuredData) schemas.push(structuredData);

    return schemas;
  };

  const schemas = getEnhancedStructuredData();

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      
      {/* Enhanced Keywords and SEO meta tags */}
      <meta name="keywords" content={keywords} />
      <meta name="author" content="London Secondary Glazing" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1" />
      <meta name="bingbot" content="index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1" />
      
      {/* Content Language */}
      <meta name="language" content="en-GB" />
      <meta httpEquiv="content-language" content="en-gb" />
      
      {/* Cache Control */}
      <meta httpEquiv="Cache-Control" content="public, max-age=31536000" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonical} />
      
      {/* Alternate language versions */}
      {alternates?.map((alt, index) => (
        <link key={index} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}
      
      {/* Enhanced Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:secure_url" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - London Secondary Glazing`} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:site_name" content="London Secondary Glazing" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:updated_time" content={lastModified || new Date().toISOString()} />
      
      {/* Enhanced Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={`${title} - London Secondary Glazing`} />
      <meta name="twitter:site" content="@ldnsecondaryglazing" />
      <meta name="twitter:creator" content="@ldnsecondaryglazing" />
      
      {/* Additional SEO meta tags */}
      <meta name="theme-color" content="#1e40af" />
      <meta name="msapplication-navbutton-color" content="#1e40af" />
      <meta name="apple-mobile-web-app-status-bar-style" content="#1e40af" />
      
      {/* Preload critical resources */}
      <link rel="preload" href="/og-image.png" as="image" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* DNS prefetch for external resources */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      
      {/* Structured data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </script>
      ))}
    </Helmet>
  );
}